import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { X } from 'lucide-react';
import { PlayerSearchFiltersProps, CoachAgeGroup } from './types'; // Import CoachAgeGroup type

const PlayerSearchFilters = ({
    genderFilter,
    setGenderFilter,
    ageGroupFilter,
    setAgeGroupFilter,
    positionsFilter,
    handlePositionFilterChange,
    competitivenessFilter,
    handleCompetitivenessFilterChange,
    coachAgeGroups,
    loadingAgeGroups,
    positionsOptions,
    competitivenessOptions,
    clearFilters,
    handleSearch,
    isSearchingPlayers,
}: PlayerSearchFiltersProps) => {

    // Helper to render age group items grouped by sex
    const renderAgeGroupItems = (sex: 'male' | 'female') => {
        const groups = coachAgeGroups
            .filter(cag => cag.age_group?.sex === sex)
            .sort((a, b) => a.age_group!.group_name.localeCompare(b.age_group!.group_name)); // Sort for consistency

        if (groups.length === 0) return null;

        return (
            <>
                <SelectItem value={`${sex}-header`} disabled className="font-bold text-gray-500 mt-1">
                    {sex === 'male' ? 'Boys' : 'Girls'}
                </SelectItem>
                {groups.map(cag => (
                    <SelectItem key={cag.age_group_id} value={cag.age_group_id}>
                        {cag.age_group?.group_name}
                    </SelectItem>
                ))}
            </>
        );
    };

    return (
        <div className="mb-6 p-4 border rounded-lg bg-gray-50 shadow-sm">
            <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-medium">Filters</h3>
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs">
                        <X className="h-3.5 w-3.5 mr-1" /> Clear All
                    </Button>
                    <Button variant="default" size="sm" onClick={handleSearch} disabled={isSearchingPlayers} className="text-xs">
                        Apply & Search
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Gender Filter */}
                <div>
                    <label htmlFor="gender-select" className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                    <Select
                        value={genderFilter}
                        onValueChange={setGenderFilter}
                    >
                        <SelectTrigger id="gender-select" className="w-full bg-white">
                            <SelectValue placeholder="All Genders" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Genders</SelectItem>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Age Group Filter */}
                <div>
                    <label htmlFor="age-group-select" className="block text-sm font-medium text-gray-700 mb-1">Age Group</label>
                    {loadingAgeGroups ? (
                        <div className="h-[40px] flex items-center justify-center bg-gray-100 border rounded-md">
                            <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-indigo-600"></div>
                        </div>
                    ) : coachAgeGroups.length === 0 ? (
                        <div className="h-[40px] flex items-center justify-center bg-gray-100 border rounded-md text-xs text-gray-500 px-2">
                            No age groups assigned.
                        </div>
                    ) : (
                        <Select
                            value={ageGroupFilter}
                            onValueChange={setAgeGroupFilter}
                        >
                            <SelectTrigger id="age-group-select" className="w-full bg-white">
                                <SelectValue placeholder="All Assigned Groups" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Assigned Groups</SelectItem>
                                {renderAgeGroupItems('male')}
                                {renderAgeGroupItems('female')}
                            </SelectContent>
                        </Select>
                    )}
                </div>

                {/* Positions Filter */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Positions</label>
                    <div className="space-y-2 mt-1">
                        {positionsOptions.map(position => (
                            <div key={position} className="flex items-center space-x-2">
                                <Checkbox
                                    id={`pos-${position}`}
                                    checked={positionsFilter.includes(position)}
                                    onCheckedChange={(checked) => handlePositionFilterChange(checked, position)}
                                />
                                <label htmlFor={`pos-${position}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                    {position}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Competitiveness Filter */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Competitiveness</label>
                    <div className="space-y-2 mt-1">
                        {competitivenessOptions.map(level => (
                            <div key={level} className="flex items-center space-x-2">
                                <Checkbox
                                    id={`comp-${level}`}
                                    checked={competitivenessFilter.includes(level)}
                                    onCheckedChange={(checked) => handleCompetitivenessFilterChange(checked, level)}
                                />
                                <label htmlFor={`comp-${level}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                    {level}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlayerSearchFilters;